import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Button,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Box,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { api as axios } from '../../api';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import url from '../../serverUrl';
import Transactions from '../Transactions/transaction.table';
import ProChart from '../../components/ProChart/ProChart';
import { logoWrapper } from '../../utils';
import SwapDiv from '../../components/SwapDiv';
import symbols from '../../symbolsMapper';
import TreasuryBackingPublic from '../../components/TreasuryBackingPublic';
import {
  COMPOSITION_TYPE_CODES,
  LIQUIDITY_TYPE_CODES,
  ASSET_TYPE_CODES,
  COLLATERALIZATION_LEVEL_TYPE_CODES,
  REBALANCING_TYPE_CODES,
  COMPLIANCE_TYPE_CODES,
  GEOGRAPHIC_REGION_CODES,
  RISK_PROFILE_CODES,
  FEE_TYPE_CODES,
  MECHANISM_TYPE_CODES,
  TRANSPARENCY_TYPE_CODES,
  WEIGHT_TYPE_CODES,
  STRATEGY_TYPE_CODES,
  TREASURY_MGMT_CODES
} from '../../screens/editForms/dbtDictionary';

const formatPrice = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(5) : '0.00000';
};

// API fields like `issuers` / `asset_types` may arrive as a real array,
// a JSON-encoded string, a plain string, or be missing entirely — normalize
// all of those to an array instead of assuming JSON.parse is safe to call.
const parseArrayField = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [value];
    } catch (e) {
      return [value];
    }
  }
  return [];
};

const DetailRow = ({ label, value, color = '#f0f0f0' }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.75,
      gap: 2
    }}
  >
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', minWidth: '40%' }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color, textAlign: 'right', flex: 1, wordBreak: 'break-word' }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const DetailBlock = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: '#f0f0f0', wordBreak: 'break-word', lineHeight: 1.6 }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const resolveLabel = (code, list) => {
  if (!code || !Array.isArray(list)) return 'N/A';
  return list.find((i) => i.code == code)?.label || code;
};

const fmtUsd = (v) =>
  Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export default function DBTPage() {
  const [data, setData] = useState({});
  const { identitySymbol } = useParams();
  const tokenSymbol = identitySymbol;
  const [assets, setAssets] = useState({ pecu: 0, tokens: [] });
  const [token, setToken] = useState(data);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data && data.id) {
      setToken(data);
    }
  }, [data]);

  useEffect(() => {
    if (tokenSymbol) {
      setLoading(true);
      axios
        .get(`${url}/dat/get_dat?symbol=${tokenSymbol}`)
        .then((res) => {
          if (res.data.status) {
            const token = res.data.stock;
            setData(token[0]);
          }
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
        });

      axios
        .get(`${url}/dat/balance/${tokenSymbol}`)
        .then((res) => {
          if (res.data) {
            setAssets(res.data);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [tokenSymbol]);

  const priceChange = data.currentPrice - data.price1hour;
  // % change is relative to the earlier price, not the current one.
  const priceChangePercent = Math.abs(
    (priceChange / (data.price1hour || data.currentPrice)) * 100
  );
  const isPositive = priceChange > 0;

  return (
    <>
      <Helmet>
        <title>{`${identitySymbol} ${
          Number.isFinite(Number(data?.currentPrice))
            ? Number(data?.currentPrice).toFixed(5)
            : ''
        }`}</title>
      </Helmet>

      {loading && (
        <Box>
          <LinearProgress sx={{ backgroundColor: 'grey' }} />
        </Box>
      )}

      {!loading && (
        <Box sx={{ p: { xs: 2, md: 4 }, ml: { xs: 4 } }}>
          <Grid container spacing={3} sx={{ ml: '-55px' }}>
            <SwapDiv token={tokenSymbol} />

            {/* Left Column - Token Info */}
            <Grid item xs={12} md={5} mt={4} sx={{ order: { xs: 1, md: 0 } }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: 'rgba(4, 10, 8, 0.72)',
                  borderRadius: 3,
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                {/* Token Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={logoWrapper(token?.logo, token)}
                    alt={token.symbol}
                    sx={{ width: 56, height: 56 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}
                    >
                      {token?.identitySymbol}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                      {token?.name}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Price Section */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#9ca3af',
                      mb: 1.5,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    {token?.symbol} Price
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 2,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ color: 'white', fontWeight: 600 }}
                    >
                      ${formatPrice(data?.currentPrice)}
                    </Typography>
                    {data.currentPrice && data.price1hour && (
                      <Chip
                        icon={
                          isPositive ? (
                            <ArrowUpwardIcon sx={{ fontSize: '16px' }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: '16px' }} />
                          )
                        }
                        label={`${priceChangePercent.toFixed(2)}%`}
                        size="medium"
                        sx={{
                          backgroundColor: isPositive
                            ? 'rgba(16, 185, 129, 0.15)'
                            : 'rgba(239, 83, 80, 0.15)',
                          color: isPositive ? '#10B981' : '#EF5350',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          '& .MuiChip-icon': {
                            color: 'inherit'
                          }
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Holdings */}
                <Box
                  sx={{
                    border: '1px solid #10B981',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)',
                    maxHeight: '250px',
                    overflowY: 'auto'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Holdings
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5
                    }}
                  >
                    <Avatar
                      src="https://dev.hootdex.org/hootdex/images/pecuCoin.png"
                      sx={{ width: 24, height: 24 }}
                    />
                    <Typography variant="body2" sx={{ color: '#f0f0f0' }}>
                      PECU: {Number(assets?.pecu || 0).toLocaleString()}
                      {assets?.pecuPrice > 0 &&
                        ` ($${fmtUsd(assets.pecu * assets.pecuPrice)})`}
                    </Typography>
                  </Box>

                  {assets?.tokens?.map((token, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                      }}
                    >
                      <Avatar
                        src={`${url}/logo/tokenlogo/${
                          symbols[token.symbol] ?? token.symbol
                        }`}
                        sx={{ width: 24, height: 24 }}
                      />
                      <Typography variant="body2" sx={{ color: '#f0f0f0' }}>
                        {token.symbol}: {Number(token.amount).toLocaleString()}
                        {token.value > 0 && ` ($${fmtUsd(token.value)})`}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Basic Information */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Basic Information
                  </Typography>
                  <Stack
                    spacing={0}
                    divider={
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                    }
                  >
                    <DetailRow
                      label="Issuers"
                      value={
                        parseArrayField(data?.issuers).length
                          ? parseArrayField(data?.issuers).join(', ')
                          : 'N/A'
                      }
                    />
                    <DetailRow
                      label="Currency"
                      value={data?.currency || 'USD'}
                    />
                    <DetailRow
                      label="Status"
                      value={data?.status === 1 ? 'Active' : 'Inactive'}
                      color={data?.status === 1 ? '#10B981' : '#EF5350'}
                    />
                  </Stack>
                </Box>

                {/* Strategy & Risk */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Strategy & Risk Profile
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Investment Strategy"
                        value={resolveLabel(
                          data?.strategy_type_code,
                          STRATEGY_TYPE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Risk Profile"
                        value={resolveLabel(
                          data?.risk_profile_code,
                          RISK_PROFILE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Rebalancing Frequency"
                        value={resolveLabel(
                          data?.rebalancing_type_code,
                          REBALANCING_TYPE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Liquidity Type"
                        value={resolveLabel(
                          data?.liquidity_type_code,
                          LIQUIDITY_TYPE_CODES
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Fees & Economics */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Fees & Economics
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Fee Structure"
                        value={resolveLabel(
                          data?.fee_type_code,
                          FEE_TYPE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Management Fee"
                        value={
                          data?.management_fee
                            ? `${data.management_fee}%`
                            : 'N/A'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Performance Fee"
                        value={
                          data?.performance_fee
                            ? `${data.performance_fee}%`
                            : 'N/A'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="AUM"
                        value={
                          data?.aum ? `$${data.aum.toLocaleString()}` : 'N/A'
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Action Buttons */}
                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    color="success"
                    href="https://pecuscan.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      borderWidth: 2,
                      fontWeight: 600,
                      '&:hover': {
                        borderWidth: 2
                      }
                    }}
                  >
                    View on Pecuscan
                  </Button>

                  {token?.link && (
                    <Button
                      variant="contained"
                      href={token.link}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        backgroundColor: '#10B981',
                        color: 'white',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#059669'
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  )}
                </Stack>
              </Paper>
            </Grid>

            {/* Right Column - Chart and Extended Details */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{ marginTop: { xs: 4, md: 4 }, order: { xs: 0, md: 1 } }}
            >
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: 'rgba(4, 10, 8, 0.72)',
                  borderRadius: 3,
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                {/* Chart Component */}
                <Box sx={{ minHeight: 400 }}>
                  <ProChart symbol={identitySymbol} chartSymbol={token?.symbol} />
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* Description */}
                {token?.description && (
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: '#10B981', mb: 2, fontWeight: 700 }}
                    >
                      About
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#f0f0f0', lineHeight: 1.8 }}
                    >
                      {token.description}
                    </Typography>
                  </Box>
                )}

                <TreasuryBackingPublic tokenType="DAT" tokenId={data?.id} />

                {/* Composition & Structure */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Composition & Structure
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Composition Type"
                        value={resolveLabel(
                          data?.composition_type_code,
                          COMPOSITION_TYPE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Underlying Assets"
                        value={
                          parseArrayField(data?.asset_types).length
                            ? parseArrayField(data?.asset_types).join(', ')
                            : 'N/A'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Collateralization Level"
                        value={resolveLabel(
                          data?.collateralization_level_code,
                          COLLATERALIZATION_LEVEL_TYPE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Weight Type"
                        value={resolveLabel(
                          data?.weight_type_code,
                          WEIGHT_TYPE_CODES
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Mechanism & Transparency */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Mechanism & Transparency
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Creation/Redemption"
                        value={resolveLabel(
                          data?.mechanism_type_code,
                          MECHANISM_TYPE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Min Creation Unit"
                        value={
                          data?.min_creation_unit?.toLocaleString() || 'N/A'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Transparency Type"
                        value={resolveLabel(
                          data?.transparency_type_code,
                          TRANSPARENCY_TYPE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Treasury Management"
                        value={resolveLabel(
                          data?.treasury_mgmt_code,
                          TREASURY_MGMT_CODES
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Compliance & Region */}
                <Box
                  sx={{
                    border: '0px solid #10B981',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(16, 185, 129, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#10B981',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Compliance & Region
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Compliance Type"
                        value={resolveLabel(
                          data?.compliance_type_code,
                          COMPLIANCE_TYPE_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Geographic Region"
                        value={resolveLabel(
                          data?.region_code,
                          GEOGRAPHIC_REGION_CODES
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="Inception Date"
                        value={
                          data?.createdAt
                            ? new Date(data.createdAt).toLocaleDateString(
                                'en-US',
                                {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                }
                              )
                            : 'N/A'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailBlock
                        label="NAV"
                        value={
                          data?.nav && Number.isFinite(Number(data.nav))
                            ? `$${Number(data.nav).toFixed(4)}`
                            : 'N/A'
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* Transactions Table - Full Width */}
            <Grid item xs={12} mt={2} sx={{ order: { xs: 2, md: 2 } }}>
              <Transactions />
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}
